import { registerDeviceApi, updateDeviceMetaApi } from '@/modules';
import { useAppStateContext } from '@/shared';
import { useImmer } from 'use-immer';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { version } from '../../../package.json';
import { deviceInfoWithAppVersion } from '../functions';

export const useUtilFunctions = () => {
  const [state, setState] = useImmer({
    isDeviceDetailsFetching: false,
  });
  const { appState, setAppState } = useAppStateContext();
  const navigate = useNavigate();

  // =================================================================== API SECTION =====================================================================================
  const registerDevice = async ({
    deviceId,
    deviceInfo,
    forceReload = false,
  }) => {
    triggerDeviceLoading(true);
    try {
      const response = await registerDeviceApi({
        deviceId,
        deviceInfo: JSON.stringify(deviceInfo),
      });
      const { data } = response;
      if (response.status === 200) {
        const deviceInfo = {
          name: data.name,
          accessToken: data.accessToken,
          deviceId: data.deviceId,
          branchId: data.branchId,
          groupId: data.groupId,
          timeZone: data.branch.timeZone,
          orientation: data.orientation,
        };

        setAppState((draft) => {
          draft.deviceInfo = deviceInfo;
        });

        toast.success('Device saved successfully');

        if (forceReload) {
          window.location.reload();
        } else {
          navigate('/', { replace: true });
        }
      } else {
        throw response;
      }
    } catch (error) {
      const message = error.response?.data?.message ?? 'Unable to connect';
      toast.error(message);
    } finally {
      triggerDeviceLoading(false);
    }
  };

  const updateDeviceMeta = async ({ deviceId }) => {
    try {
      // current device info with updated app version
      const deviceInfo = deviceInfoWithAppVersion();

      const isSameBrowser =
        appState?.deviceMeta?.isBrowser &&
        appState?.deviceMeta?.browserFullVersion ===
          deviceInfo?.browserFullVersion;

      // Only update if the version or browser has changed
      if (version === appState?.deviceMeta?.appVersion && isSameBrowser) return;

      const response = await updateDeviceMetaApi({
        deviceId,
        deviceInfo: JSON.stringify(deviceInfo),
      });

      if (response.status === 200) {
        setAppState((draft) => {
          draft.deviceMeta = deviceInfo;
        });
      } else {
        throw response;
      }
    } catch (error) {
      console.error('Error updating device meta', error);
    }
  };

  // ================================================================================= UTIL FUNCTION SECTION =====================================================================================
  const triggerDeviceLoading = (loading) => {
    setState((draft) => {
      draft.isDeviceDetailsFetching = loading;
    });
  };

  return {
    ...state,
    registerDevice,
    updateDeviceMeta,
  };
};
